import * as R from "ramda";

import {
  lockRoute,
  setScheduleRequired,
  updateStop,
  setRequests,
  setStops,
  setSaveRouteEnable,
} from "./route";
import {
  getRouteDetail,
  isRouteStatusLoading,
  getCurrentRouteId,
  getRouteRequests,
  getRouteStops,
  getRequestOrder,
  isRouteReadyOrOpen,
} from "../selectors/route";
import {
  getStopByNumber,
  updateStopPostcodeData,
  updateStopTime,
  validatePostcode,
  validateStopTime,
} from "../../features/routeDetail/model";
import * as postcodeService from "../../services/postcode";
import * as stopService from "../../services/stop";
import { DISMISS, INVALID_POSTCODE } from "../../constants/strings";
import { formatSecondsToMMSS } from "../../utils/date";
import { getSelectedDepot } from "../selectors/app";
import { generateMoveParcelRequests } from "../../models/request";
import {
  getSelectedPreparedStops,
  getSelectedStops,
} from "../selectors/ui/routeDetail";
import { getMergeToParcelList } from "../../models/stop";
import { showNotification } from "./notifications";
import { NotificationType } from "../../features/Notifier";
import {
  getSortedStops,
  replaceMergeTargetAndRemoveSelected,
  updateSeqAndDisplayNumbers,
} from "../../models/moveStops";
import { updateSelectedStops } from "./ui/routeDetail";
import { getErrorMessage } from "../../utils/errors";
import { removeWhiteSpace } from "../../utils/string";

export const handleStopTimeChange = (
  inputValue,
  { setFieldError, resetForm },
  { stopTime, stopNumber }
) => async (dispatch, getState) => {
  if (inputValue === formatSecondsToMMSS(stopTime)) {
    resetForm();
    return;
  }
  const currentUserLock = await dispatch(lockRoute());

  const state = getState();

  if (!currentUserLock) {
    resetForm();
    return;
  }

  const validationError = validateStopTime(inputValue);

  if (!!validationError) {
    setFieldError(validationError);
    return;
  }

  const routeDetail = getRouteDetail(state);
  const stop = getStopByNumber(routeDetail.stop, stopNumber);

  const updatedStop = updateStopTime(stop, inputValue);

  dispatch(updateStop(updatedStop));
  dispatch(setScheduleRequired(true));
  dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
};

export const handleStopPostcodeChange = (
  inputValue,
  { setFieldError, resetForm },
  { postcode, stopNumber }
) => async (dispatch, getState) => {
  if (inputValue === postcode) {
    resetForm();
    return;
  }

  const currentUserLock = await dispatch(lockRoute());

  const state = getState();

  if (!currentUserLock) {
    resetForm();
    return;
  }

  try {
    const postcode = removeWhiteSpace(inputValue);
    const validationError = validatePostcode(postcode);

    if (!!validationError) {
      setFieldError(INVALID_POSTCODE);
      return;
    }

    const postcodeData = await postcodeService.getPostcodeLocation(postcode);
    const routeDetail = getRouteDetail(state);
    const stop = getStopByNumber(routeDetail.stop, stopNumber);

    const updatedStop = updateStopPostcodeData(stop, postcodeData);

    dispatch(updateStop(updatedStop));
    dispatch(setScheduleRequired(true));
    dispatch(setSaveRouteEnable(isRouteReadyOrOpen(state)));
  } catch {
    setFieldError(INVALID_POSTCODE);
  }
};

export const updateKingParcel = ({ rowData }) => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return;
  }

  const state = getState();
  const routeDetail = getRouteDetail(state);
  const { stopNumber } = getSelectedStops(state)[0];
  const stop = getStopByNumber(routeDetail.stop, stopNumber);
  const parcel = stop.parcel.slice();
  const kingParcelIndex = parcel.findIndex(
    ({ parcelIndex }) => parcelIndex === 1
  );
  const selectedParcelIndex = parcel.findIndex(
    ({ parcelCode }) => parcelCode === rowData.parcelCode
  );

  if (kingParcelIndex > -1) {
    parcel[kingParcelIndex] = {
      ...parcel[kingParcelIndex],
      parcelIndex: parcel[selectedParcelIndex].parcelIndex,
    };
  }

  parcel[selectedParcelIndex] = {
    ...parcel[selectedParcelIndex],
    parcelIndex: 1,
  };

  dispatch(updateStop({ ...stop, parcel }));
  dispatch(setScheduleRequired(true));
};

export const onChangeBulkStop = () => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  const state = getState();
  const { stopNumber } = getSelectedPreparedStops(state)[0];

  if (!currentUserLock) {
    return;
  }

  const routeDetail = getRouteDetail(state);
  const stop = getStopByNumber(routeDetail.stop, stopNumber);

  dispatch(
    updateStop({
      ...stop,
      bulk: !stop.bulk,
    })
  );
  dispatch(setScheduleRequired(true));
};

export const validateStopMerge = ({
  parcels,
  checkOpenRequests,
  shopUnsuitable = false,
  splitStop = false,
}) => (dispatch, getState) => {
  const state = getState();
  const depotCode = getSelectedDepot(state);
  const routeDetail = getRouteDetail(state);
  const filteredParcels = [...new Set(parcels)]; // remove duplicate parcelCodes for cases like stop transfer

  return stopService.validateStopMerge(
    depotCode,
    routeDetail.routeId,
    filteredParcels,
    checkOpenRequests,
    shopUnsuitable,
    splitStop
  );
};

export const createMoveParcelsRequests = (parcelList, fromStop, toStop) => (
  dispatch,
  getState
) => {
  const state = getState();
  const routeId = getCurrentRouteId(state);
  const requestOrder = getRequestOrder(state);

  if (isRouteStatusLoading(state)) {
    const newRequests = generateMoveParcelRequests(
      parcelList,
      fromStop,
      toStop,
      routeId,
      requestOrder
    );
    const requests = getRouteRequests(state);

    dispatch(setRequests(requests.concat(newRequests)));
  }
};

export const onMergeStopPick = (pickedStop) => async (dispatch, getState) => {
  const selectedStops = getSelectedStops(getState());
  const parcels = getMergeToParcelList(selectedStops, pickedStop);

  try {
    return await dispatch(
      validateStopMerge({ parcels, checkOpenRequests: true })
    );
  } catch (error) {
    dispatch(
      showNotification({
        key: "merge-stop-message",
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
      })
    );
    return null;
  }
};

export const mergeStops = (newStop) => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return;
  }

  const state = getState();

  const isLoading = isRouteStatusLoading(state);
  const stops = getRouteStops(state);
  const selectedStops = getSelectedStops(state);
  const { routeId, request: requests } = getRouteDetail(state);
  const requestOrder = getRequestOrder(state);

  dispatch(updateSelectedStops([newStop.stopId]));
  if (isLoading) {
    const newRequests = R.flatten(
      selectedStops.map((stop) =>
        generateMoveParcelRequests(
          stop.parcel,
          stop,
          newStop,
          routeId,
          requestOrder
        )
      )
    );
    dispatch(setRequests(requests.concat(newRequests)));
  }

  const sortedStops = getSortedStops(stops);
  const replacedStopList = replaceMergeTargetAndRemoveSelected(
    sortedStops,
    selectedStops,
    newStop
  );

  const updatedStopList = updateSeqAndDisplayNumbers(replacedStopList, true);

  dispatch(setStops(updatedStopList));
  dispatch(setScheduleRequired(true));
};

export const removeStop = ({ stopNumber }) => (dispatch, getState) => {
  const stops = getRouteStops(getState());
  const updatedStopList = stops.filter(
    (stop) => stop.stopNumber !== stopNumber
  );
  dispatch(setStops(updatedStopList));
};

export const processRequestStop = (stop, requests) => (dispatch) => {
  const newParcels = stop?.parcel.filter(
    ({ parcelCode }) =>
      !requests.find((request) => request.parcelCode === parcelCode)
  );

  if (newParcels.length) {
    dispatch(updateStop({ ...stop, parcel: newParcels }));
  } else {
    dispatch(removeStop(stop));
  }
};
